import { getDatabaseContainerType, type AnalyticsClient } from '@databases/analytics';
import { logError } from '@databases/logs';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import type { JiraIssueRef, ResolvedJiraIssueRef } from '@databases/types/confluence';
import { loadJiraIssue, addJiraIssuesToList } from '@databases/utils/redux/jira/actions';
import type { AnyAction } from 'redux';

import {
  executeObjectResolverAction,
  type ExecuteActionRequest
} from '@canvas/atlassian-service/datasource';
import { smartLinkClient } from '@canvas/smart-link-client';

type LoadJiraIssueThunk = ReturnType<typeof loadJiraIssue>;

export interface UpdateSummaryParams {
  issueRef: JiraIssueRef;
  resolvedIssue: ResolvedJiraIssueRef;
  newSummary: string;
  originalSummary: string;
  dispatch: (
    action: LoadJiraIssueThunk | AnyAction
  ) => Promise<ResolvedJiraIssueRef | undefined> | AnyAction;
  abortSignal?: AbortSignal;
  contentId: string;
  analytics: AnalyticsClient;
}

export const updateJiraSummary = async (params: UpdateSummaryParams): Promise<void> => {
  const {
    issueRef,
    resolvedIssue,
    newSummary,
    originalSummary,
    dispatch,
    abortSignal,
    contentId,
    analytics
  } = params;

  const trimmed = newSummary.trim();
  const original = originalSummary.trim();
  if (trimmed === original) {
    return;
  }
  if (!resolvedIssue.ari) {
    return;
  }

  const buildUpdatedIssue = (summaryText: string): ResolvedJiraIssueRef =>
    ({
      cloudId: issueRef.cloudId,
      serverId: issueRef.serverId,
      ...resolvedIssue,
      summary: summaryText,
      columns: {
        ...resolvedIssue.columns,
        summary: {
          ...resolvedIssue.columns?.summary,
          text: summaryText
        }
      }
    }) as ResolvedJiraIssueRef;

  // Apply optimistic update
  if (!abortSignal?.aborted) {
    dispatch(addJiraIssuesToList([buildUpdatedIssue(trimmed)]));
  }

  try {
    await executeObjectResolverAction({
      integrationKey: 'jira',
      actionKey: 'atlassian:work-item:update:summary',
      parameters: {
        inputs: { summary: trimmed },
        target: { ari: resolvedIssue.ari }
      }
    } as ExecuteActionRequest);

    // Send analytics event for successful summary update
    analytics.sendJiraFieldUpdatedEvent(
      {
        contentId,
        issueKey: issueRef.key,
        fieldType: 'summary',
        databaseContainer: getDatabaseContainerType()
      },
      'jiraSummary',
      'jiraSummaryValueEdit'
    );
  } catch (e) {
    if (abortSignal?.aborted) {
      return; // Silently return if aborted
    }

    logError(
      'jira-summary/update:failed',
      e instanceof Error ? e : new Error('jira summary update failed')
    );

    // Rollback optimistic update
    if (!abortSignal?.aborted) {
      dispatch(addJiraIssuesToList([buildUpdatedIssue(original)]));
    }

    currentSnackbarState.setMessage('Failed to update summary', {
      id: 'jira-summary-update-failed',
      type: 'error',
      showCloseButton: true,
      hideAfter: 4000
    });
    return;
  } finally {
    const reloadedIssue = await dispatch(
      loadJiraIssue(
        { key: issueRef.key, cloudId: issueRef.cloudId, serverId: issueRef.serverId },
        { columns: ['summary'], forceReload: true }
      )
    );
    if (reloadedIssue?.url) {
      smartLinkClient.forceRefreshData(reloadedIssue.url);
    }
  }
};
